"use client";

import { useLayoutEffect, useRef, useSyncExternalStore } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { CONSENT_CHANGE_EVENT, GA_ID, readConsent, updateConsent, writeConsent, type ConsentChoice } from "@/lib/analytics";
import { consentBannerHeight } from "@/lib/consent-banner-size";
import { localizedPublicPath } from "@/lib/public-route-slugs";

/**
 * Cookie banner for the analytics consent gate (Consent Mode v2).
 *
 * Shown once, only when analytics is configured and the visitor has not chosen
 * yet. A compact, fixed bar: full-width slim bar on mobile (~2 lines), a small
 * ~360px card bottom-LEFT on desktop. Left, not right: on the 96 commerce pages
 * the package picker is the right-hand column, and a bottom-right card covered
 * 30-60% of the lowest visible package card on first paint (measured live at
 * 1024/1440 on 2026-09-04); bottom-left touched no CTA on those pages. Checkout
 * is reached after the banner was already dismissed on the landing page, so its
 * layout is not the one to optimise the first paint for.
 *
 * It must never cover a page CTA. Because it is `position: fixed`, it would
 * otherwise float over checkout buttons, hero CTAs, and package pickers. The
 * fix is to reserve exactly the bar's height as `padding-bottom` on <body> while
 * it is open, so every CTA can be scrolled clear of the bar. Padding added at
 * the very end of the document only extends the scroll area below the last
 * element — nothing already on screen reflows. The reservation follows native
 * ResizeObserver measurements without transitions and clears on close.
 *
 * Accessibility: real buttons with 44px targets, a landmark label, and Decline
 * kept exactly as easy as Accept — the two buttons are visually equal (no
 * dark pattern).
 */
/**
 * The shared analytics helper emits this event for local choices and cross-tab
 * storage changes. Keep the subscription stable across renders.
 */
const subscribeToConsent = (notify: () => void) => {
    window.addEventListener(CONSENT_CHANGE_EVENT, notify);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, notify);
};
/** Client snapshot: the banner is needed only when analytics is configured and no choice is stored. */
const getConsentSnapshot = () => Boolean(GA_ID) && readConsent() === null;
/** Server snapshot: localStorage does not exist during SSR, so render nothing. */
const getConsentServerSnapshot = () => false;

export default function CookieConsent({ locale }: { locale: string }) {
    const t = useTranslations("consent");
    // Read the stored choice through useSyncExternalStore rather than setting
    // state from an effect on mount: the server snapshot renders the banner
    // closed (so SSR and hydration agree) and the client snapshot decides
    // visibility during render, with no cascading second render.
    const open = useSyncExternalStore(subscribeToConsent, getConsentSnapshot, getConsentServerSnapshot);
    const ref = useRef<HTMLElement>(null);

    // Reserve the bar's height at the bottom of the page so no CTA stays hidden
    // under the fixed bar. Kept in sync with the bar's real height (locale text
    // length, viewport width, orientation all change it) and cleared on close.
    useLayoutEffect(() => {
        if (!open) return;
        const el = ref.current;
        if (!el) return;
        let previousSpace = "";
        const ro = new ResizeObserver(entries => {
            const entry = entries.find(item => item.target === el);
            if (!entry) return;
            const height = consentBannerHeight(entry, () => getComputedStyle(el));
            if (height === null) return;
            // Preserve the existing 16px allowance around the measured banner.
            const space = `${height + 16}px`;
            if (space === previousSpace) return;
            previousSpace = space;
            document.body.style.paddingBottom = space;
            document.body.style.setProperty("--cookie-consent-space", space);
        });
        // Install before paint; the initial delivery supplies completed layout.
        // No timer, resize polling or read-after-write offsetHeight is needed.
        ro.observe(el, { box: "border-box" });
        return () => {
            ro.disconnect();
            document.body.style.paddingBottom = "";
            document.body.style.removeProperty("--cookie-consent-space");
        };
    }, [open]);

    if (!open) return null;

    const choose = (choice: ConsentChoice) => {
        writeConsent(choice);
        updateConsent(choice);
    };

    return (
        <section
            ref={ref}
            aria-label={t("ariaLabel")}
            className="fixed inset-x-2 bottom-2 z-[70] rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-xl sm:inset-x-auto sm:left-4 sm:bottom-4 sm:w-[360px]"
        >
            <div className="flex flex-col gap-2.5 sm:gap-3">
                <p className="text-sm leading-snug text-slate-700">
                    <span className="font-semibold text-slate-900">{t("title")}.</span>{" "}
                    {t("text")}{" "}
                    {/* prefetch off (ST-12 / PERF-07): a policy link in a banner is not
                        where a visitor goes next; prefetching it cost a request per view. */}
                    <Link
                        prefetch={false}
                        href={localizedPublicPath(locale, "cookies")}
                        className="font-semibold text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
                    >
                        {t("policy")}
                    </Link>
                </p>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => choose("granted")}
                        className="min-h-[44px] flex-1 rounded-lg bg-emerald-700 px-4 text-sm font-bold text-white hover:bg-emerald-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
                    >
                        {t("accept")}
                    </button>
                    <button
                        type="button"
                        onClick={() => choose("denied")}
                        className="min-h-[44px] flex-1 rounded-lg border-2 border-slate-300 px-4 text-sm font-bold text-slate-800 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
                    >
                        {t("reject")}
                    </button>
                </div>
            </div>
        </section>
    );
}
